/**
 * BrandImportModal: bulk-upload a brand's catalogue from a workbook.
 * ───────────────────────────────────────────────────────────────────────────
 * The four steps, the review table, the progress bar, the run log and the
 * never-close-mid-write rule are CatalogueImportModal, shared with every other
 * vertical. What is left here is what only a brand has: the brand endpoints,
 * the brand's own spec template, and a template download carrying that brand's
 * spec columns.
 *
 * Nothing about this component is brand-specific in the sense of naming one.
 * It takes whichever brand the page is showing, and per-brand attributes ride
 * in `spec:` columns.
 *
 * Mounted from views/Publications/BrandProductManagement.js, which is itself
 * routed twice: once under /admin for Lusuku staff and once under the brand
 * owner's own "My Brand" tree. One mount, both audiences; the backend's
 * requireBrandCatalogAccess scopes every write either way.
 */
import React, { useCallback } from "react";
import axios from "axios";

import { buildImportPlan } from "../../utilities/brandImport.js";
import { runBrandImport } from "../../utilities/brandImportRunner.js";
import { downloadTemplateWorkbook } from "../../utilities/brandImportTemplate.js";
import { uploadImageToCloudinary } from "../../utilities/imageUpload.js";
import { getTokenFromLocalStorage } from "../../utilities/tokenUtils.js";
import CatalogueImportModal from "./CatalogueImportModal.js";

const API_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

/** The brand accent, unchanged. Not the dashboard's standard action colour. */
const ACCENT = "#17385a";

const authHeaders = () => {
  const token = getTokenFromLocalStorage();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BrandImportModal = ({ show, brand, existingSections, onClose, onImported }) => {
  const brandId = brand?._id;

  /* Memoised because the shared modal loads it from an effect keyed on the
     function itself; a fresh closure every render would re-fetch forever. */
  const loadExtras = useCallback(async () => {
    const headers = authHeaders();
    const [categories, units, brandDoc] = await Promise.all([
      fetch("/category/get-all")
        .then((r) => r.json())
        .catch(() => []),
      fetch("/unit")
        .then((r) => r.json())
        .catch(() => []),
      axios
        .get(`${API_URL}/api/brands/${brandId}`, { headers })
        .then((r) => r.data?.data || r.data)
        .catch(() => null),
    ]);
    return {
      platformCategories: Array.isArray(categories) ? categories : [],
      units: Array.isArray(units) ? units : [],
      specTemplate: brandDoc?.productSpecTemplate || null,
    };
  }, [brandId]);

  return (
    <CatalogueImportModal
      show={show}
      vendor={brand}
      existingSections={existingSections}
      onClose={onClose}
      onImported={onImported}
      accent={ACCENT}
      vendorNoun="brand"
      title={`Bulk import products — ${brand?.name || "this brand"}`}
      buildPlan={buildImportPlan}
      runImport={(plan, deps) => runBrandImport(plan, { ...deps, brand: deps.vendor })}
      loadExtras={loadExtras}
      templateLabel={`Download the template for ${brand?.name || "this brand"}`}
      onDownloadTemplate={({ extras, existingSections: sections }) =>
        downloadTemplateWorkbook({
          brandName: brand?.name || "",
          specFieldKeys: (extras.specTemplate?.fields || [])
            .map((f) => f.fieldKey)
            .filter(Boolean),
          sectionTitles: (sections || []).map((s) => s.title).filter(Boolean),
          categoryNames: (extras.platformCategories || [])
            .map((c) => c.category)
            .filter(Boolean),
          unitNames: (extras.units || []).map((u) => u.unit).filter(Boolean),
        })
      }
      api={{
        createSection: (id, body) =>
          axios.post(`${API_URL}/api/brands/${id}/sections`, body, {
            headers: authHeaders(),
          }),
        fetchSections: (id) =>
          axios.get(`${API_URL}/api/brands/${id}/sections`, { headers: authHeaders() }),
        createProduct: (id, sectionId, body) =>
          axios.post(`${API_URL}/api/brands/${id}/sections/${sectionId}/products`, body, {
            headers: authHeaders(),
          }),
        uploadImage: uploadImageToCloudinary,
      }}
      pickHelp={
        <>
          Fill the <strong>1. Sections</strong> and <strong>2. Products</strong> sheets,
          then upload the workbook here. Working in Google Sheets is fine, use File then
          Download then Microsoft Excel (.xlsx). Photos are matched to the{" "}
          <strong>Image File</strong> column, ignoring capitalisation. Rows marked out of
          stock are skipped, and anything already on this brand is skipped too, so
          re-running the same workbook never creates duplicates.
        </>
      }
    />
  );
};

export default BrandImportModal;
